import React, { useEffect } from 'react';
import { FirebaseUser } from '../types/user';
import './stylesheets/SelectedUsersReadQuestions.css';

interface SelectedUsersReadQuestionsProps {
  userData: {
    uid: string;
    readQuestions: string[];
    lastReadAt?: string;
  };
  user: FirebaseUser | null;
  onClose: () => void;
}

const SelectedUsersReadQuestions: React.FC<SelectedUsersReadQuestionsProps> = ({
  userData,
  user,
  onClose
}) => {
  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore body scroll
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Parse date helper
  const parseDate = (dateString: string): Date => {
    return new Date(dateString);
  };

  // Check if mobile
  const isMobile = window.innerWidth <= 768;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">User Reading Activity</h2>
            <div className={`user-info-row ${isMobile ? 'mobile' : ''}`}>
              <span className="user-name">
                <strong>Name:</strong> {user?.name || user?.email || 'Unknown User'}
              </span>
              <span className="user-email">
                <strong>Email:</strong> {user?.email || 'N/A'}
              </span>
              <span className="user-id">
                <strong>ID:</strong> 
                <span style={{
                  fontFamily: 'monospace', 
                  fontSize: '12px',
                  wordBreak: 'break-all',
                  display: 'inline-block',
                  marginLeft: '4px'
                }}>
                  {userData.uid}
                </span>
              </span>
              <span className="questions-count">
                <strong>Questions Read:</strong> {userData.readQuestions.length}
              </span>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={`modal-body ${isMobile ? 'mobile' : ''}`}>
          {/* User Metadata */}
          <div className="user-metadata">
            <div className="metadata-item">
              <div className="metadata-label">Total Questions Read</div>
              <div className="metadata-value">{userData.readQuestions.length}</div>
            </div>
            <div className="metadata-item">
              <div className="metadata-label">Account Created</div>
              <div className="metadata-value">
                {user?.createdAt ? (
                  <>
                    <div>{user.createdAt.toDate().toLocaleDateString()}</div>
                    <div style={{fontSize: '14px', color: '#b0b0b0'}}>
                      {user.createdAt.toDate().toLocaleTimeString()}
                    </div>
                  </>
                ) : (
                  <span style={{color: '#888', fontStyle: 'italic'}}>Unknown</span>
                )}
              </div>
            </div>
          </div>

          {/* Questions Section */}
          <div className={`section ${isMobile ? 'mobile' : ''}`}>
            <h3 className={`section-title ${isMobile ? 'mobile' : ''}`}>
              Read Question IDs ({userData.readQuestions.length})
            </h3>
            
            {userData.readQuestions.length === 0 ? (
              <div className="no-data">
                This user has not read any questions yet.
              </div>
            ) : (
              <div className="questions-container">
                <div className="questions-grid">
                  {userData.readQuestions.map((questionId, index) => (
                    <div key={questionId} className="question-id-card">
                      <div className="question-index">#{index + 1}</div>
                      <div className="question-id-text">{questionId}</div>
                    </div>
                  ))}
                </div>
                
                {/* Summary Info */}
                <div className="questions-summary">
                  <div className="summary-item">
                    <span className="summary-label">Total Questions:</span>
                    <span className="summary-value">{userData.readQuestions.length}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Unique Question IDs:</span>
                    <span className="summary-value">{new Set(userData.readQuestions).size}</span>
                  </div>
                  {userData.readQuestions.length !== new Set(userData.readQuestions).size && (
                    <div className="summary-item warning">
                      <span className="summary-label">⚠️ Duplicates Found:</span>
                      <span className="summary-value">
                        {userData.readQuestions.length - new Set(userData.readQuestions).size}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectedUsersReadQuestions;